/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from "react";
import { getApp } from "firebase/app";

const TOOL_LIST = ["pen","marker","highlighter","eraser","text","arrow","rectangle","circle","line"];

const COLORS = [
  { name: "black",  hex: "#1a1a1a" },
  { name: "blue",   hex: "#1CB0F6" },
  { name: "red",    hex: "#FF4B4B" },
  { name: "green",  hex: "#58CC02" },
  { name: "orange", hex: "#FF9600" },
  { name: "purple", hex: "#CE82FF" },
];

const SIZES = [
  { label:"S",  pen:2,  marker:6,  hl:12, eraser:12, line:2, shape:2, text:14 },
  { label:"M",  pen:4,  marker:12, hl:20, eraser:24, line:3, shape:3, text:20 },
  { label:"L",  pen:7,  marker:20, hl:32, eraser:40, line:5, shape:5, text:28 },
  { label:"XL", pen:12, marker:32, hl:50, eraser:70, line:8, shape:8, text:40 },
];

const MAX_HIST = 40;

const VOCAB_DICT = {
  hello:      { ipa:"/həˈloʊ/",       ar:"مرحباً",      ex:"Hello! How are you today?" },
  goodbye:    { ipa:"/ˌɡʊdˈbaɪ/",    ar:"وداعاً",      ex:"Goodbye! See you tomorrow." },
  please:     { ipa:"/pliːz/",        ar:"من فضلك",     ex:"Please sit down." },
  thank:      { ipa:"/θæŋk/",         ar:"يشكر",        ex:"Thank you very much." },
  sorry:      { ipa:"/ˈsɒri/",        ar:"آسف",         ex:"I am sorry for the mistake." },
  today:      { ipa:"/təˈdeɪ/",       ar:"اليوم",       ex:"Today we learn about verbs." },
  tomorrow:   { ipa:"/təˈmɒroʊ/",    ar:"غداً",        ex:"Tomorrow is a new day." },
  yesterday:  { ipa:"/ˈjestədeɪ/",   ar:"أمس",         ex:"Yesterday I went to school." },
  learn:      { ipa:"/lɜːrn/",        ar:"يتعلم",       ex:"We learn English every day." },
  write:      { ipa:"/raɪt/",         ar:"يكتب",        ex:"Please write your name." },
  read:       { ipa:"/riːd/",         ar:"يقرأ",        ex:"I love to read books." },
  speak:      { ipa:"/spiːk/",        ar:"يتكلم",       ex:"Can you speak English?" },
  listen:     { ipa:"/ˈlɪsən/",      ar:"يستمع",       ex:"Please listen carefully." },
  understand: { ipa:"/ˌʌndəˈstænd/", ar:"يفهم",        ex:"Do you understand the lesson?" },
  example:    { ipa:"/ɪɡˈzɑːmpəl/",  ar:"مثال",        ex:"Give me an example please." },
  work:       { ipa:"/wɜːrk/",        ar:"يعمل",        ex:"She works at a hospital." },
  time:       { ipa:"/taɪm/",         ar:"وقت",         ex:"What time is it?" },
  school:     { ipa:"/skuːl/",        ar:"مدرسة",       ex:"I go to school every day." },
  english:    { ipa:"/ˈɪŋɡlɪʃ/",    ar:"الإنجليزية",  ex:"English is very useful." },
  grammar:    { ipa:"/ˈɡræmər/",      ar:"قواعد",       ex:"We study grammar every week." },
};

const ERROR_CARDS = [
  { id:"tobe",     title:"Missing TO BE",      wrong:"She tired",          right:"She IS tired",        note:"Subject + to be + adjective" },
  { id:"article",  title:"Missing Article",    wrong:"I have car",         right:"I have A car",         note:"Countable singular needs a/an" },
  { id:"hisher",   title:"His / Her",          wrong:"His name is Layla",  right:"HER name is Layla",    note:"Her = feminine" },
  { id:"adjnoun",  title:"Adjective Order",    wrong:"car red",            right:"red car",              note:"Adjective comes BEFORE noun" },
  { id:"thirdp",   title:"3rd Person -S",      wrong:"She work",           right:"She workS",            note:"He/She/It + verb + s" },
];

function rdp(pts, eps) {
  if (pts.length <= 2) return pts;
  let dmax = 0, idx = 0;
  const n = pts.length - 1;
  const [x1,y1,x2,y2] = [pts[0].x,pts[0].y,pts[n].x,pts[n].y];
  const len = Math.hypot(x2-x1, y2-y1);
  for (let i = 1; i < n; i++) {
    const d = len === 0
      ? Math.hypot(pts[i].x-x1, pts[i].y-y1)
      : Math.abs((y2-y1)*pts[i].x - (x2-x1)*pts[i].y + x2*y1 - y2*x1) / len;
    if (d > dmax) { dmax = d; idx = i; }
  }
  if (dmax > eps) {
    const a = rdp(pts.slice(0, idx+1), eps);
    const b = rdp(pts.slice(idx), eps);
    return [...a.slice(0,-1), ...b];
  }
  return [pts[0], pts[n]];
}

function detectShape(pts) {
  if (pts.length < 4) return null;
  const xs = pts.map(p=>p.x), ys = pts.map(p=>p.y);
  const [minX,maxX,minY,maxY] = [Math.min(...xs),Math.max(...xs),Math.min(...ys),Math.max(...ys)];
  const w = maxX-minX, h = maxY-minY;
  const diag = Math.hypot(w,h);
  if (diag < 15) return null;

  const first = pts[0], last = pts[pts.length-1];
  const closeGap = Math.hypot(last.x-first.x, last.y-first.y);
  const isClosed = closeGap < diag * 0.35;

  if (isClosed) {
    const simp = rdp(pts, diag * 0.1);
    const corners = simp.filter((p,i) => {
      if (i === 0) return true;
      if (i === simp.length-1)
        return Math.hypot(p.x-simp[0].x, p.y-simp[0].y) > diag*0.15;
      return true;
    });
    if (corners.length >= 3 && corners.length <= 4) {
      return { type:"triangle", minX, minY, maxX, maxY };
    }
    const cx=(minX+maxX)/2, cy=(minY+maxY)/2, rx=w/2, ry=h/2;
    if (rx>0 && ry>0) {
      const err = pts.reduce((s,p)=>s+Math.abs(Math.hypot((p.x-cx)/rx,(p.y-cy)/ry)-1),0)/pts.length;
      if (err < 0.28) return { type:"ellipse", cx, cy, rx, ry };
    }
    return { type:"rectangle", minX, minY, maxX, maxY };
  } else {
    const lineLen = Math.hypot(last.x-first.x, last.y-first.y);
    if (lineLen < 20) return null;
    const avgDev = pts.reduce((s,p)=>s+Math.abs(
      (last.y-first.y)*p.x-(last.x-first.x)*p.y+last.x*first.y-last.y*first.x
    )/lineLen, 0)/pts.length;
    if (avgDev < lineLen*0.08)
      return { type:"line", x1:first.x, y1:first.y, x2:last.x, y2:last.y };
    return null;
  }
}

const TOOL_ICONS = {
  pen:"✏️", marker:"🖊️", highlighter:"🖍️", eraser:"⌫",
  text:"T", arrow:"↗", rectangle:"▭", circle:"○", line:"╱",
};

export default function SmartWhiteboard() {
  const [tool, setTool]             = useState("pen");
  const [color, setColor]           = useState("#1a1a1a");
  const [sizeIdx, setSizeIdx]       = useState(1);
  const [autoCorrect, setAutoCorrect] = useState(true);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [histLen, setHistLen]       = useState(0);
  const [rightOpen, setRightOpen]   = useState(true);
  const [rightTab, setRightTab]     = useState("templates");
  const [toast, setToast]           = useState(null);
  const [mousePos, setMousePos]     = useState({x:0,y:0});
  const [lastSaved, setLastSaved]   = useState(null);
  const [isDirty, setIsDirty]       = useState(false);
  const [sessionId]                 = useState(()=>`wb_${Date.now()}`);
  const [saving, setSaving]         = useState(false);
  const [textInput, setTextInput]   = useState(null);
  const [textValue, setTextValue]   = useState("");
  const [stickies, setStickies]     = useState([]);
  const [stickyDraft, setStickyDraft] = useState("");
  const draggingSticky              = useRef(null);
  const dragOffset                  = useRef({x:0,y:0});
  const [vocabQuery, setVocabQuery] = useState("");
  const [vocabResult, setVocabResult] = useState(null);

  const canvasRef   = useRef(null);
  const containerRef= useRef(null);
  const textareaRef = useRef(null);
  const isDrawing   = useRef(false);
  const startPt     = useRef({x:0,y:0});
  const pathPts     = useRef([]);
  const snapshot    = useRef(null);
  const historyRef  = useRef([]);
  const autoSaveRef = useRef(null);

  const sz = SIZES[sizeIdx];

  const getCtx = useCallback(()=> canvasRef.current?.getContext("2d"), []);

  const showToast = useCallback((msg)=>{
    setToast(msg);
    setTimeout(()=>setToast(null), 2200);
  },[]);

  const markDirty = useCallback(()=> setIsDirty(true),[]);

  const takeSnapshot = useCallback(()=>{
    const ctx = getCtx();
    const c = canvasRef.current;
    if (!ctx||!c) return null;
    return ctx.getImageData(0, 0, c.width, c.height);
  },[getCtx]);

  const pushHistory = useCallback(()=>{
    const img = takeSnapshot();
    if (!img) return;
    if (historyRef.current.length >= MAX_HIST) historyRef.current.shift();
    historyRef.current.push(img);
    setHistLen(historyRef.current.length);
    markDirty();
  },[takeSnapshot, markDirty]);

  const undo = useCallback(()=>{
    const ctx = getCtx();
    if (!ctx || historyRef.current.length === 0) return;
    const prev = historyRef.current.pop();
    ctx.putImageData(prev, 0, 0);
    setHistLen(historyRef.current.length);
  },[getCtx]);

  const clearBoard = useCallback(()=>{
    const ctx = getCtx(); const c = canvasRef.current; if (!ctx||!c) return;
    pushHistory();
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    setShowClearConfirm(false);
    showToast("Board cleared");
  },[getCtx, pushHistory, showToast]);

  useEffect(()=>{
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas||!container) return;

    const init = ()=>{
      const saved = canvas.width>0 ? getCtx()?.getImageData(0,0,canvas.width,canvas.height) : null;
      canvas.width  = container.clientWidth;
      canvas.height = container.clientHeight;
      const ctx = getCtx();
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (saved) ctx.putImageData(saved, 0, 0);
    };

    init();
    const ro = new ResizeObserver(init);
    ro.observe(container);
    return ()=> ro.disconnect();
  },[getCtx]);

  useEffect(()=>{
    const onKey = e=>{
      if (e.ctrlKey && e.key==="z") { e.preventDefault(); undo(); }
      if (e.key==="Escape") {
        setTextInput(null); setTextValue("");
        setShowClearConfirm(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return ()=> window.removeEventListener("keydown", onKey);
  },[undo]);

  useEffect(()=>{
    if (!isDirty) return;
    clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(()=> saveToFirebase(), 120000);
    return ()=> clearTimeout(autoSaveRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isDirty]);

  const applyStyle = useCallback((ctx)=>{
    ctx.globalCompositeOperation = "source-over";
    ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.globalAlpha = 1;
    switch(tool){
      case "pen":         ctx.strokeStyle=color; ctx.lineWidth=sz.pen; break;
      case "marker":      ctx.strokeStyle=color; ctx.lineWidth=sz.marker; break;
      case "highlighter": ctx.strokeStyle=color; ctx.lineWidth=sz.hl; ctx.globalAlpha=0.35; break;
      case "arrow": case "line": ctx.strokeStyle=color; ctx.lineWidth=sz.line; break;
      case "rectangle": case "circle": ctx.strokeStyle=color; ctx.lineWidth=sz.shape; break;
      default: ctx.strokeStyle=color; ctx.lineWidth=sz.pen;
    }
  },[tool,color,sz]);

  const drawArrow = useCallback((ctx,x1,y1,x2,y2)=>{
    const head = Math.max(sz.line*4, 12);
    const ang  = Math.atan2(y2-y1, x2-x1);
    ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(x2,y2); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x2,y2);
    ctx.lineTo(x2-head*Math.cos(ang-Math.PI/6), y2-head*Math.sin(ang-Math.PI/6));
    ctx.lineTo(x2-head*Math.cos(ang+Math.PI/6), y2-head*Math.sin(ang+Math.PI/6));
    ctx.closePath(); ctx.fillStyle=color; ctx.globalAlpha=1; ctx.fill();
  },[color,sz.line]);

  const getPos = (e)=>{
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const sx=c.width/r.width, sy=c.height/r.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x:(cx-r.left)*sx, y:(cy-r.top)*sy, screenX:cx, screenY:cy };
  };

  const onMouseDown = useCallback(e=>{
    if (e.button===2) return;
    const ctx = getCtx(); if (!ctx) return;
    const pos = getPos(e);

    if (tool==="text") {
      const r = canvasRef.current.getBoundingClientRect();
      setTextInput({ screenX:pos.screenX-r.left, screenY:pos.screenY-r.top, canvasX:pos.x, canvasY:pos.y });
      setTextValue("");
      setTimeout(()=> textareaRef.current?.focus(), 30);
      return;
    }

    if (tool==="eraser") {
      pushHistory();
      isDrawing.current = true;
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, sz.eraser/2, 0, Math.PI*2);
      ctx.fill();
      return;
    }

    const img = takeSnapshot();
    snapshot.current = img;
    if (img) {
      if (historyRef.current.length >= MAX_HIST) historyRef.current.shift();
      historyRef.current.push(img);
      setHistLen(historyRef.current.length);
      markDirty();
    }

    pathPts.current = [pos];
    startPt.current = pos;
    isDrawing.current = true;
    applyStyle(ctx);

    if (tool==="pen"||tool==="marker"||tool==="highlighter") {
      ctx.beginPath(); ctx.moveTo(pos.x,pos.y);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tool, sz, getCtx, takeSnapshot, pushHistory, applyStyle, showToast, markDirty]);

  const onMouseMove = useCallback(e=>{
    const pos = getPos(e);
    setMousePos({x:Math.round(pos.x), y:Math.round(pos.y)});

    if (!isDrawing.current) return;
    const ctx = getCtx(); if (!ctx) return;

    if (tool==="eraser") {
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, sz.eraser/2, 0, Math.PI*2);
      ctx.fill();
      return;
    }

    pathPts.current.push(pos);

    if (tool==="pen"||tool==="marker"||tool==="highlighter") {
      applyStyle(ctx);
      ctx.lineTo(pos.x,pos.y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(pos.x,pos.y);
      return;
    }

    if (snapshot.current) ctx.putImageData(snapshot.current, 0, 0);
    applyStyle(ctx);
    const {x:x1,y:y1} = startPt.current;
    const {x:x2,y:y2} = pos;

    if (tool==="line") {
      ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(x2,y2); ctx.stroke();
    } else if (tool==="arrow") {
      drawArrow(ctx,x1,y1,x2,y2);
    } else if (tool==="rectangle") {
      ctx.beginPath(); ctx.strokeRect(x1,y1,x2-x1,y2-y1);
    } else if (tool==="circle") {
      ctx.beginPath();
      ctx.ellipse((x1+x2)/2,(y1+y2)/2,Math.abs(x2-x1)/2,Math.abs(y2-y1)/2,0,0,Math.PI*2);
      ctx.stroke();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tool, sz, getCtx, applyStyle, drawArrow]);

  const onMouseUp = useCallback(e=>{
    if (!isDrawing.current) return;
    isDrawing.current = false;
    const ctx = getCtx(); if (!ctx) return;
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;

    if (autoCorrect && (tool==="pen"||tool==="marker") && pathPts.current.length > 5) {
      const shape = detectShape(pathPts.current);
      if (shape) {
        if (snapshot.current) ctx.putImageData(snapshot.current, 0, 0);
        applyStyle(ctx);
        ctx.globalAlpha = 1;
        if (shape.type==="line") {
          ctx.beginPath(); ctx.moveTo(shape.x1,shape.y1); ctx.lineTo(shape.x2,shape.y2); ctx.stroke();
          showToast("✓ Corrected to line");
        } else if (shape.type==="ellipse") {
          ctx.beginPath(); ctx.ellipse(shape.cx,shape.cy,shape.rx,shape.ry,0,0,Math.PI*2); ctx.stroke();
          showToast("✓ Corrected to ellipse");
        } else if (shape.type==="triangle") {
          const {minX,minY,maxX,maxY}=shape;
          ctx.beginPath();
          ctx.moveTo((minX+maxX)/2, minY);
          ctx.lineTo(minX, maxY);
          ctx.lineTo(maxX, maxY);
          ctx.closePath(); ctx.stroke();
          showToast("✓ Corrected to triangle");
        } else if (shape.type==="rectangle") {
          const {minX,minY,maxX,maxY}=shape;
          ctx.beginPath(); ctx.strokeRect(minX,minY,maxX-minX,maxY-minY);
          showToast("✓ Corrected to rectangle");
        }
      }
    }
    pathPts.current = [];
    markDirty();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tool, autoCorrect, getCtx, applyStyle, showToast, markDirty]);

  const commitText = useCallback(()=>{
    if (!textValue.trim()||!textInput) { setTextInput(null); return; }
    const ctx = getCtx(); if (!ctx) return;
    pushHistory();
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.fillStyle = color;
    ctx.font = `bold ${sz.text}px Nunito, sans-serif`;
    ctx.fillText(textValue, textInput.canvasX, textInput.canvasY);
    setTextInput(null); setTextValue("");
    markDirty();
  },[textValue, textInput, color, sz, getCtx, pushHistory, markDirty]);

  const saveAsPng = useCallback(()=>{
    const c = canvasRef.current; if (!c) return;
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `whiteboard_${Date.now()}.png`;
    a.click();
  },[]);

  const saveToFirebase = useCallback(async()=>{
    const c = canvasRef.current; if (!c) return;
    setSaving(true);
    try {
      const { getStorage, ref, uploadString } = await import("firebase/storage");
      const storage = getStorage(getApp());
      const dataUrl = c.toDataURL("image/png");
      const base64 = dataUrl.split(",")[1];
      const path = `whiteboards/${sessionId}/${Date.now()}.png`;
      const storageRef = ref(storage, path);
      await uploadString(storageRef, base64, "base64", { contentType:"image/png" });
      setLastSaved(new Date());
      setIsDirty(false);
      showToast("Board saved ✓");
    } catch(err) {
      showToast("Save failed: " + err.message);
    }
    setSaving(false);
  },[sessionId, showToast]);

  const loadFromFirebase = useCallback(async()=>{
    try {
      const { getStorage, ref, listAll, getDownloadURL } = await import("firebase/storage");
      const storage = getStorage(getApp());
      const listRef = ref(storage, `whiteboards/${sessionId}`);
      const res = await listAll(listRef);
      if (res.items.length===0) { showToast("No saved board found"); return; }
      const latest = res.items[res.items.length-1];
      const url = await getDownloadURL(latest);
      const img = new Image(); img.crossOrigin="anonymous";
      img.onload = ()=>{
        const ctx = getCtx(); const c = canvasRef.current; if (!ctx||!c) return;
        pushHistory();
        ctx.drawImage(img, 0, 0);
        showToast("Board loaded ✓");
      };
      img.src = url;
    } catch(err) {
      showToast("Load failed: " + err.message);
    }
  },[sessionId, getCtx, pushHistory, showToast]);

  const drawTemplate = useCallback((type)=>{
    const ctx = getCtx(); const c = canvasRef.current; if (!ctx||!c) return;
    pushHistory();
    ctx.globalCompositeOperation = "source-over"; ctx.globalAlpha = 1;
    const W=c.width, H=c.height;
    const pad=40, fw=W-pad*2, fh=H-pad*2;
    ctx.strokeStyle="#1a1a1a"; ctx.lineWidth=2; ctx.lineCap="round"; ctx.lineJoin="round";
    ctx.font="bold 14px Nunito,sans-serif"; ctx.fillStyle="#1a1a1a";

    if (type==="tense") {
      const cols=["",  "Past Simple","Present Simple","Present Perfect","Future Simple"];
      const rows=["","Example 1","Example 2","Negative","Question","Key words"];
      const cw=(fw-60)/4, ch=(fh-30)/5, ox=pad+60, oy=pad+30;
      ctx.strokeStyle="#555"; ctx.lineWidth=1.5;
      for(let r=0;r<=5;r++) {
        ctx.beginPath(); ctx.moveTo(pad, oy+r*ch); ctx.lineTo(W-pad, oy+r*ch); ctx.stroke();
      }
      for(let c2=0;c2<=4;c2++) {
        ctx.beginPath(); ctx.moveTo(ox+c2*cw, pad); ctx.lineTo(ox+c2*cw, oy+5*ch); ctx.stroke();
      }
      ctx.beginPath(); ctx.moveTo(pad,pad); ctx.lineTo(pad,oy+5*ch); ctx.stroke();
      const hColors=["#1CB0F6","#58CC02","#CE82FF","#FF9600"];
      cols.slice(1).forEach((col,i)=>{
        ctx.fillStyle=hColors[i]; ctx.font="bold 13px Nunito,sans-serif";
        ctx.fillText(col, ox+i*cw+6, pad+18);
      });
      ctx.fillStyle="#1a1a1a"; ctx.font="bold 12px Nunito,sans-serif";
      rows.slice(1).forEach((row,i)=>{ ctx.fillText(row, pad+4, oy+i*ch+ch/2+5); });
    }

    else if (type==="timeline") {
      const y=H/2, x1=pad+20, x2=W-pad-20;
      ctx.strokeStyle="#1a1a1a"; ctx.lineWidth=3;
      ctx.beginPath(); ctx.moveTo(x1,y); ctx.lineTo(x2,y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x2,y);
      ctx.lineTo(x2-12,y-7); ctx.lineTo(x2-12,y+7); ctx.closePath();
      ctx.fillStyle="#1a1a1a"; ctx.fill();
      const pts=[
        {label:"PAST",    sub:"Happened before now",     pct:0.15, col:"#FF4B4B"},
        {label:"PAST CONT",sub:"Was happening",           pct:0.32, col:"#FF9600"},
        {label:"NOW",     sub:"Present / happening now",  pct:0.5,  col:"#58CC02"},
        {label:"WILL",    sub:"Will happen",              pct:0.68, col:"#1CB0F6"},
        {label:"FUTURE",  sub:"Will happen later",        pct:0.85, col:"#CE82FF"},
      ];
      pts.forEach(pt=>{
        const px=x1+(x2-x1)*pt.pct;
        ctx.fillStyle=pt.col;
        ctx.beginPath(); ctx.arc(px,y,8,0,Math.PI*2); ctx.fill();
        ctx.fillStyle="#1a1a1a"; ctx.font="bold 13px Nunito,sans-serif";
        ctx.fillText(pt.label, px-30, y-20);
        ctx.font="11px Nunito,sans-serif"; ctx.fillStyle="#555";
        ctx.fillText(pt.sub, px-40, y+30);
      });
    }

    else if (type==="sentence") {
      const labels=["Subject","Verb","Object","Time / Place"];
      const bColors=["#DDF4FF","#D7FFB8","#FFE0E0","#FFF0CC"];
      const bBorders=["#1CB0F6","#58CC02","#FF4B4B","#FF9600"];
      const bw=(fw-30)/4, bh=120, by=H/2-60;
      labels.forEach((lbl,i)=>{
        const bx=pad+i*(bw+10);
        ctx.fillStyle=bColors[i];
        ctx.beginPath(); roundRect(ctx,bx,by,bw,bh,12); ctx.fill();
        ctx.strokeStyle=bBorders[i]; ctx.lineWidth=2.5;
        ctx.beginPath(); roundRect(ctx,bx,by,bw,bh,12); ctx.stroke();
        ctx.fillStyle=bBorders[i]; ctx.font="bold 14px Nunito,sans-serif";
        ctx.fillText(lbl, bx+bw/2-ctx.measureText(lbl).width/2, by+35);
        ctx.fillStyle="#999"; ctx.font="italic 12px Nunito,sans-serif";
        const examples=["I / She / They","go / eat / work","the book / home","every day / now"];
        ctx.fillText(examples[i], bx+bw/2-ctx.measureText(examples[i]).width/2, by+65);
      });
    }

    else if (type==="vocabgrid") {
      const headers=["English","Arabic","Example sentence"];
      const rows_n=4; const cw2=fw/3; const ch2=50; const oy2=pad+40;
      ctx.strokeStyle="#ccc"; ctx.lineWidth=1.5;
      for(let r=0;r<=rows_n;r++){
        ctx.beginPath(); ctx.moveTo(pad, oy2+r*ch2); ctx.lineTo(W-pad, oy2+r*ch2); ctx.stroke();
      }
      for(let c2=0;c2<=3;c2++){
        ctx.beginPath(); ctx.moveTo(pad+c2*cw2, pad); ctx.lineTo(pad+c2*cw2, oy2+rows_n*ch2); ctx.stroke();
      }
      const hcols=["#1CB0F6","#FF9600","#58CC02"];
      headers.forEach((h,i)=>{
        ctx.fillStyle=hcols[i]; ctx.font="bold 14px Nunito,sans-serif";
        ctx.fillText(h, pad+i*cw2+8, oy2-10);
      });
    }

    else if (type==="dialogue") {
      drawBubble(ctx, pad, pad, W*0.55-pad, H*0.42-pad, "ALI", "#DDF4FF","#1CB0F6");
      drawBubble(ctx, W*0.45, H*0.55, W*0.55-pad, H*0.42-pad, "LAYLA","#D7FFB8","#58CC02");
    }

    else if (type==="conditional") {
      const bw2=160, bh2=60;
      const ifX=(W-bw2)/2, ifY=pad+20;
      ctx.fillStyle="#FFF0CC"; ctx.strokeStyle="#FF9600"; ctx.lineWidth=2.5;
      ctx.beginPath(); roundRect(ctx,ifX,ifY,bw2,bh2,12); ctx.fill();
      ctx.beginPath(); roundRect(ctx,ifX,ifY,bw2,bh2,12); ctx.stroke();
      ctx.fillStyle="#FF9600"; ctx.font="bold 16px Nunito,sans-serif";
      ctx.fillText("IF condition...", ifX+16, ifY+37);
      const my=ifY+bh2+40;
      const lx=W*0.2, rx=W*0.7;
      ctx.strokeStyle="#555"; ctx.lineWidth=2;
      ctx.beginPath(); ctx.moveTo(W/2,ifY+bh2); ctx.lineTo(W/2,my-10); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(W/2,my-10); ctx.lineTo(lx+80,my-10); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(W/2,my-10); ctx.lineTo(rx+80,my-10); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(lx+80,my-10); ctx.lineTo(lx+80,my); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(rx+80,my-10); ctx.lineTo(rx+80,my); ctx.stroke();
      ctx.fillStyle="#D7FFB8"; ctx.strokeStyle="#58CC02"; ctx.lineWidth=2.5;
      ctx.beginPath(); roundRect(ctx,lx,my,160,70,12); ctx.fill();
      ctx.beginPath(); roundRect(ctx,lx,my,160,70,12); ctx.stroke();
      ctx.fillStyle="#46A302"; ctx.font="bold 13px Nunito,sans-serif";
      ctx.fillText("✓ Result (TRUE)", lx+10, my+28);
      ctx.fillStyle="#555"; ctx.font="12px Nunito,sans-serif";
      ctx.fillText("then... / will...", lx+10, my+50);
      ctx.fillStyle="#FFE0E0"; ctx.strokeStyle="#FF4B4B"; ctx.lineWidth=2.5;
      ctx.beginPath(); roundRect(ctx,rx,my,160,70,12); ctx.fill();
      ctx.beginPath(); roundRect(ctx,rx,my,160,70,12); ctx.stroke();
      ctx.fillStyle="#DD2222"; ctx.font="bold 13px Nunito,sans-serif";
      ctx.fillText("✗ No result (FALSE)", rx+10, my+28);
      ctx.fillStyle="#555"; ctx.font="12px Nunito,sans-serif";
      ctx.fillText("otherwise / unless...", rx+10, my+50);
    }

    markDirty();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[getCtx, pushHistory, markDirty]);

  const stampError = useCallback((card)=>{
    const ctx = getCtx(); const c = canvasRef.current; if(!ctx||!c) return;
    pushHistory();
    ctx.globalCompositeOperation="source-over"; ctx.globalAlpha=1;
    const W=c.width, H=c.height;
    const bw=280, bh=130, bx=W/2-bw/2, by=H/2-bh/2;
    ctx.fillStyle="#1a1a1a"; ctx.fillRect(bx-2,by-2,bw+4,bh+4);
    ctx.fillStyle="#fff"; ctx.fillRect(bx,by,bw,bh);
    ctx.fillStyle="#2D2D2D";
    ctx.fillRect(bx,by,bw,32);
    ctx.fillStyle="#fff"; ctx.font="bold 13px Nunito,sans-serif";
    ctx.fillText("⚠️ "+card.title, bx+10, by+21);
    ctx.fillStyle="#FF4B4B"; ctx.font="bold 15px Nunito,sans-serif";
    ctx.fillText("✗ "+card.wrong, bx+10, by+56);
    ctx.strokeStyle="#FF4B4B"; ctx.lineWidth=1.5;
    const wm = ctx.measureText("✗ "+card.wrong);
    ctx.beginPath(); ctx.moveTo(bx+10, by+59); ctx.lineTo(bx+10+wm.width, by+59); ctx.stroke();
    ctx.fillStyle="#555"; ctx.font="18px sans-serif";
    ctx.fillText("→", bx+10, by+85);
    ctx.fillStyle="#58CC02"; ctx.font="bold 15px Nunito,sans-serif";
    ctx.fillText("✓ "+card.right, bx+34, by+85);
    ctx.fillStyle="#777"; ctx.font="italic 11px Nunito,sans-serif";
    ctx.fillText(card.note, bx+10, by+115);
    markDirty();
  },[getCtx, pushHistory, markDirty]);

  const stampVocab = useCallback(()=>{
    if (!vocabResult) return;
    const ctx = getCtx(); const c = canvasRef.current; if(!ctx||!c) return;
    pushHistory();
    const bw=260, bh=110, bx=60, by=60;
    ctx.fillStyle="#F4E7FF";
    ctx.beginPath(); roundRect(ctx,bx,by,bw,bh,14); ctx.fill();
    ctx.strokeStyle="#CE82FF"; ctx.lineWidth=2.5;
    ctx.beginPath(); roundRect(ctx,bx,by,bw,bh,14); ctx.stroke();
    ctx.fillStyle="#9B59B6"; ctx.font="bold 18px Nunito,sans-serif";
    ctx.fillText(vocabQuery, bx+12, by+28);
    ctx.fillStyle="#777"; ctx.font="13px Nunito,sans-serif";
    ctx.fillText(vocabResult.ipa, bx+12, by+48);
    ctx.fillStyle="#1a1a1a"; ctx.font="14px Nunito,sans-serif";
    ctx.fillText(vocabResult.ar, bx+12, by+68);
    ctx.fillStyle="#555"; ctx.font="italic 12px Nunito,sans-serif";
    const ex = vocabResult.ex.length>38 ? vocabResult.ex.slice(0,38)+"…" : vocabResult.ex;
    ctx.fillText(ex, bx+12, by+92);
    markDirty();
  },[vocabQuery, vocabResult, getCtx, pushHistory, markDirty]);

  const onStickyMouseDown = (e, id)=>{
    e.preventDefault();
    draggingSticky.current = id;
    const sticky = stickies.find(s=>s.id===id);
    dragOffset.current = { x:e.clientX-sticky.x, y:e.clientY-sticky.y };
    const onMove = e2=>{
      setStickies(ss=>ss.map(s=>s.id===id ? {...s, x:e2.clientX-dragOffset.current.x, y:e2.clientY-dragOffset.current.y} : s));
    };
    const onUp = ()=>{
      draggingSticky.current=null;
      window.removeEventListener("mousemove",onMove);
      window.removeEventListener("mouseup",onUp);
    };
    window.addEventListener("mousemove",onMove);
    window.addEventListener("mouseup",onUp);
  };

  const lookupVocab = ()=>{
    const key = vocabQuery.toLowerCase().trim();
    setVocabResult(VOCAB_DICT[key] || null);
  };

  const fmtTime = (d)=>{
    if (!d) return "Never";
    return d.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"});
  };

  return (
    <div style={{display:"flex",height:"100%",background:"#1E1E2E",overflow:"hidden",fontFamily:"Nunito,sans-serif",userSelect:"none"}}>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* ── Top Toolbar ──────────────────────────────────────────────── */}
        <div style={{background:"#252540",borderBottom:"1px solid #2D2D4E",padding:"6px 10px",display:"flex",alignItems:"center",gap:8,flexShrink:0,flexWrap:"wrap"}}>

          {/* Tool buttons */}
          <div style={{display:"flex",gap:3,background:"#1E1E2E",borderRadius:8,padding:"3px"}}>
            {TOOL_LIST.map(t=>(
              <button key={t} onClick={()=>setTool(t)} title={t}
                style={{
                  padding:"5px 8px",borderRadius:6,border:"none",cursor:"pointer",
                  fontSize:t==="text"?13:15,fontWeight:t==="text"?"bold":"normal",
                  background:tool===t?"#00CD9C22":"none",
                  color:tool===t?"#00CD9C":"#bbb",outline:"none",transition:"all 0.15s",
                }}
              >{TOOL_ICONS[t]}</button>
            ))}
          </div>

          <div style={{width:1,height:28,background:"#2D2D4E"}} />

          {/* Colors */}
          <div style={{display:"flex",gap:4}}>
            {COLORS.map(c=>(
              <button key={c.hex} onClick={()=>setColor(c.hex)} title={c.name}
                style={{
                  width:22,height:22,borderRadius:"50%",background:c.hex,border:"none",cursor:"pointer",
                  boxShadow:color===c.hex?`0 0 0 2px #fff, 0 0 0 4px ${c.hex}`:"0 0 0 2px #0000",
                  transition:"box-shadow 0.15s",outline:"none",
                }}
              />
            ))}
          </div>

          <div style={{width:1,height:28,background:"#2D2D4E"}} />

          {/* Sizes */}
          <div style={{display:"flex",gap:3,background:"#1E1E2E",borderRadius:8,padding:"3px"}}>
            {SIZES.map((s,i)=>(
              <button key={s.label} onClick={()=>setSizeIdx(i)}
                style={{
                  padding:"3px 10px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,
                  background:sizeIdx===i?"#00CD9C22":"none",color:sizeIdx===i?"#00CD9C":"#bbb",outline:"none",
                }}
              >{s.label}</button>
            ))}
          </div>

          <div style={{width:1,height:28,background:"#2D2D4E"}} />

          {/* Auto-correct toggle */}
          <button onClick={()=>setAutoCorrect(v=>!v)}
            style={{
              padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,
              background:autoCorrect?"#00CD9C22":"#1E1E2E",color:autoCorrect?"#00CD9C":"#666",outline:"none",
            }}
          >✦ Auto-correct {autoCorrect?"ON":"OFF"}</button>

          <div style={{width:1,height:28,background:"#2D2D4E"}} />

          {/* Undo / Clear / Save */}
          <button onClick={undo} disabled={histLen===0}
            style={{padding:"4px 10px",borderRadius:6,border:"none",cursor:histLen===0?"not-allowed":"pointer",
              fontSize:13,background:"#1E1E2E",color:histLen===0?"#444":"#bbb",outline:"none"}}
            title="Undo (Ctrl+Z)"
          >↩ Undo ({histLen})</button>

          {showClearConfirm ? (
            <div style={{display:"flex",alignItems:"center",gap:4,background:"#FF4B4B22",border:"1px solid #FF4B4B55",borderRadius:8,padding:"2px 6px"}}>
              <span style={{fontSize:12,color:"#FF4B4B",fontWeight:700}}>Clear all?</span>
              <button onClick={clearBoard}
                style={{padding:"3px 10px",borderRadius:6,border:"none",cursor:"pointer",
                  fontSize:12,background:"#FF4B4B",color:"#fff",fontWeight:700,outline:"none"}}
              >Yes</button>
              <button onClick={()=>setShowClearConfirm(false)}
                style={{padding:"3px 8px",borderRadius:6,border:"none",cursor:"pointer",
                  fontSize:12,background:"#1E1E2E",color:"#bbb",outline:"none"}}
              >No</button>
            </div>
          ) : (
            <button onClick={()=>setShowClearConfirm(true)}
              style={{padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",
                fontSize:12,background:"#FF4B4B22",color:"#FF4B4B",fontWeight:700,outline:"none"}}
              title="Clear entire board"
            >🗑 Clear All</button>
          )}

          <button onClick={saveAsPng}
            style={{padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",
              fontSize:12,background:"#1CB0F622",color:"#1CB0F6",fontWeight:700,outline:"none"}}
          >⬇ Save PNG</button>

          <button onClick={saveToFirebase} disabled={saving}
            style={{padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",
              fontSize:12,background:"#CE82FF22",color:"#CE82FF",fontWeight:700,outline:"none",
              opacity:saving?0.5:1}}
          >{saving?"Saving…":"☁ Save"}</button>

          <button onClick={loadFromFirebase}
            style={{padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",
              fontSize:12,background:"#FF960022",color:"#FF9600",fontWeight:700,outline:"none"}}
          >☁ Load</button>

          {/* Right panel toggle */}
          <button onClick={()=>setRightOpen(v=>!v)} style={{marginLeft:"auto",
            padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",
            fontSize:12,background:"#1E1E2E",color:"#bbb",outline:"none"}}
          >{rightOpen?"▶ Panel":"◀ Panel"}</button>
        </div>

        {/* ── Canvas + Right Panel row ──────────────────────────────────── */}
        <div style={{flex:1,display:"flex",overflow:"hidden"}}>

          {/* Canvas area */}
          <div ref={containerRef} style={{flex:1,position:"relative",overflow:"hidden",background:"#fff",cursor:tool==="eraser"?"crosshair":tool==="text"?"text":"crosshair"}}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            <canvas ref={canvasRef} style={{display:"block",width:"100%",height:"100%"}} />

            {/* Text input overlay */}
            {textInput && (
              <div style={{position:"absolute",left:textInput.screenX,top:textInput.screenY,zIndex:20}}>
                <textarea
                  ref={textareaRef}
                  value={textValue}
                  onChange={e=>setTextValue(e.target.value)}
                  onKeyDown={e=>{
                    if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();commitText();}
                    if(e.key==="Escape"){setTextInput(null);setTextValue("");}
                  }}
                  spellCheck="true"
                  autoCorrect="on"
                  rows={2}
                  style={{
                    minWidth:180,padding:"6px 8px",fontSize:sz.text,fontFamily:"Nunito,sans-serif",
                    color:color,background:"rgba(255,255,255,0.92)",
                    border:`2px dashed ${color}`,borderRadius:6,outline:"none",resize:"both",
                    lineHeight:1.4,boxShadow:"0 2px 12px #0003",
                  }}
                  placeholder="Type text… Enter to stamp, Esc to cancel"
                />
              </div>
            )}

            {/* Sticky notes overlay */}
            {stickies.map(s=>(
              <div key={s.id}
                onMouseDown={e=>onStickyMouseDown(e,s.id)}
                onDoubleClick={()=>setStickies(ss=>ss.filter(x=>x.id!==s.id))}
                style={{
                  position:"absolute",left:s.x,top:s.y,
                  background:"#FFF9C4",border:"1px solid #F9A825",borderRadius:8,
                  padding:"8px 10px",minWidth:120,maxWidth:200,
                  boxShadow:"2px 2px 8px #0003",cursor:"grab",zIndex:15,
                  fontSize:13,lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word",
                  fontFamily:"Nunito,sans-serif",
                }}
              >
                <div style={{fontSize:10,color:"#F9A825",marginBottom:4,fontWeight:700}}>📌 Double-click to delete</div>
                {s.text}
              </div>
            ))}

            {/* Toast notification */}
            {toast && (
              <div style={{
                position:"absolute",bottom:40,left:"50%",transform:"translateX(-50%)",
                background:"#1a1a2e",color:"#00CD9C",padding:"8px 20px",borderRadius:20,
                fontSize:13,fontWeight:700,boxShadow:"0 4px 20px #0006",zIndex:50,
                border:"1px solid #00CD9C44",pointerEvents:"none",
              }}>{toast}</div>
            )}
          </div>

          {/* ── Right Panel ────────────────────────────────────────────── */}
          {rightOpen && (
            <div style={{width:230,background:"#1A1A2E",borderLeft:"1px solid #2D2D4E",display:"flex",flexDirection:"column",flexShrink:0,overflowY:"auto"}}>

              {/* Tabs */}
              <div style={{display:"flex",borderBottom:"1px solid #2D2D4E"}}>
                {[
                  {id:"templates",icon:"📐"},
                  {id:"errors",   icon:"⚠️"},
                  {id:"vocab",    icon:"📖"},
                  {id:"sticky",   icon:"📌"},
                ].map(tab=>(
                  <button key={tab.id} onClick={()=>setRightTab(tab.id)}
                    style={{
                      flex:1,padding:"8px 4px",border:"none",cursor:"pointer",fontSize:16,
                      background:rightTab===tab.id?"#00CD9C22":"none",
                      borderBottom:rightTab===tab.id?"2px solid #00CD9C":"2px solid transparent",
                      color:rightTab===tab.id?"#00CD9C":"#555",outline:"none",
                    }}
                    title={tab.id}
                  >{tab.icon}</button>
                ))}
              </div>

              <div style={{flex:1,padding:12,overflowY:"auto"}}>

                {/* ── Templates tab ── */}
                {rightTab==="templates" && (
                  <div>
                    <div style={{color:"#777",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Teaching Templates</div>
                    {[
                      {id:"tense",       label:"📊 Tense comparison"},
                      {id:"timeline",    label:"📅 Past/Present/Future timeline"},
                      {id:"sentence",    label:"🔲 Sentence pattern"},
                      {id:"vocabgrid",   label:"📋 Vocabulary grid"},
                      {id:"dialogue",    label:"💬 Dialogue bubbles"},
                      {id:"conditional", label:"🔀 Conditional diagram"},
                    ].map(t=>(
                      <button key={t.id} onClick={()=>drawTemplate(t.id)}
                        style={{
                          display:"block",width:"100%",padding:"8px 10px",marginBottom:5,
                          background:"#252540",border:"1px solid #2D2D4E",borderRadius:8,
                          color:"#ccc",cursor:"pointer",textAlign:"left",fontSize:12,
                          transition:"background 0.15s",outline:"none",
                        }}
                        onMouseEnter={e=>e.currentTarget.style.background="#2D2D5E"}
                        onMouseLeave={e=>e.currentTarget.style.background="#252540"}
                      >{t.label}</button>
                    ))}
                  </div>
                )}

                {/* ── Error cards tab ── */}
                {rightTab==="errors" && (
                  <div>
                    <div style={{color:"#777",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Interference Errors</div>
                    {ERROR_CARDS.map(card=>(
                      <div key={card.id} onClick={()=>stampError(card)}
                        style={{
                          background:"#252540",border:"1px solid #FF4B4B44",borderRadius:8,
                          padding:"8px 10px",marginBottom:6,cursor:"pointer",transition:"background 0.15s",
                        }}
                        onMouseEnter={e=>e.currentTarget.style.background="#2D2D5E"}
                        onMouseLeave={e=>e.currentTarget.style.background="#252540"}
                      >
                        <div style={{color:"#FF9600",fontSize:11,fontWeight:700,marginBottom:3}}>⚠️ {card.title}</div>
                        <div style={{color:"#FF4B4B",fontSize:11,textDecoration:"line-through"}}>{card.wrong}</div>
                        <div style={{color:"#58CC02",fontSize:11}}>→ {card.right}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Vocabulary tab ── */}
                {rightTab==="vocab" && (
                  <div>
                    <div style={{color:"#777",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Vocabulary Lookup</div>
                    <div style={{display:"flex",gap:5,marginBottom:8}}>
                      <input
                        value={vocabQuery}
                        onChange={e=>setVocabQuery(e.target.value)}
                        onKeyDown={e=>e.key==="Enter"&&lookupVocab()}
                        placeholder="Type a word…"
                        style={{flex:1,padding:"6px 8px",borderRadius:6,border:"1px solid #2D2D4E",
                          background:"#1E1E2E",color:"#ccc",fontSize:12,outline:"none"}}
                      />
                      <button onClick={lookupVocab}
                        style={{padding:"6px 10px",borderRadius:6,border:"none",background:"#00CD9C",
                          color:"#000",fontWeight:700,fontSize:12,cursor:"pointer"}}
                      >Go</button>
                    </div>
                    {vocabResult ? (
                      <div style={{background:"#252540",border:"1px solid #CE82FF44",borderRadius:8,padding:10}}>
                        <div style={{color:"#CE82FF",fontWeight:900,fontSize:15}}>{vocabQuery}</div>
                        <div style={{color:"#888",fontSize:12,marginTop:2}}>{vocabResult.ipa}</div>
                        <div style={{color:"#ccc",fontSize:13,marginTop:4}}>{vocabResult.ar}</div>
                        <div style={{color:"#aaa",fontSize:11,fontStyle:"italic",marginTop:4}}>{vocabResult.ex}</div>
                        <button onClick={stampVocab}
                          style={{marginTop:8,padding:"5px 10px",borderRadius:6,border:"none",
                            background:"#CE82FF22",color:"#CE82FF",fontWeight:700,fontSize:11,cursor:"pointer"}}
                        >⊕ Add to board</button>
                      </div>
                    ) : vocabQuery && (
                      <div style={{color:"#555",fontSize:12}}>No result for "{vocabQuery}"</div>
                    )}
                  </div>
                )}

                {/* ── Sticky notes tab ── */}
                {rightTab==="sticky" && (
                  <div>
                    <div style={{color:"#777",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Sticky Notes</div>
                    <textarea
                      value={stickyDraft}
                      onChange={e=>setStickyDraft(e.target.value)}
                      placeholder="Type note…"
                      rows={3}
                      spellCheck="true"
                      autoCorrect="on"
                      style={{width:"100%",padding:"6px 8px",borderRadius:6,border:"1px solid #2D2D4E",
                        background:"#1E1E2E",color:"#ccc",fontSize:12,outline:"none",resize:"vertical",boxSizing:"border-box"}}
                    />
                    <button onClick={()=>{
                      if(!stickyDraft.trim()) return;
                      setStickies(ss=>[...ss,{id:Date.now(),x:100,y:100,text:stickyDraft.trim()}]);
                      setStickyDraft("");
                    }}
                      style={{marginTop:6,padding:"6px 12px",borderRadius:6,border:"none",
                        background:"#FFF9C422",color:"#F9A825",fontWeight:700,fontSize:12,cursor:"pointer",width:"100%"}}
                    >+ Add sticky note</button>
                    <div style={{marginTop:10,color:"#555",fontSize:11}}>
                      {stickies.length} note{stickies.length!==1?"s":""} on board. Drag to move. Double-click to delete.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Status Bar ───────────────────────────────────────────────── */}
        <div style={{background:"#16162A",borderTop:"1px solid #2D2D4E",padding:"4px 14px",
          display:"flex",alignItems:"center",gap:20,fontSize:11,color:"#555",flexShrink:0}}>
          <span style={{color:"#00CD9C",fontWeight:700}}>Tool: {tool}</span>
          <span>Auto-correct: <span style={{color:autoCorrect?"#00CD9C":"#555"}}>{autoCorrect?"ON":"OFF"}</span></span>
          <span>Last saved: <span style={{color:lastSaved?"#aaa":"#444"}}>{fmtTime(lastSaved)}</span></span>
          <span style={{marginLeft:"auto"}}>
            x:{mousePos.x} y:{mousePos.y}
          </span>
          <span style={{color:isDirty?"#FF9600":"#555"}}>{isDirty?"● Unsaved":"✓ Saved"}</span>
        </div>
      </div>
    </div>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x+r, y);
  ctx.lineTo(x+w-r, y); ctx.arcTo(x+w, y, x+w, y+r, r);
  ctx.lineTo(x+w, y+h-r); ctx.arcTo(x+w, y+h, x+w-r, y+h, r);
  ctx.lineTo(x+r, y+h); ctx.arcTo(x, y+h, x, y+h-r, r);
  ctx.lineTo(x, y+r); ctx.arcTo(x, y, x+r, y, r);
  ctx.closePath();
}

function drawBubble(ctx, x, y, w, h, label, bg, border) {
  ctx.fillStyle = bg; ctx.strokeStyle = border; ctx.lineWidth = 2.5;
  ctx.beginPath(); roundRect(ctx, x, y, w, h, 16); ctx.fill();
  ctx.beginPath(); roundRect(ctx, x, y, w, h, 16); ctx.stroke();
  ctx.fillStyle = border; ctx.font = "bold 12px Nunito,sans-serif";
  ctx.fillText(label, x+12, y+20);
  ctx.fillStyle="#999"; ctx.font="italic 13px Nunito,sans-serif";
  ctx.fillText("Type dialogue here…", x+12, y+h/2+5);
}
